import type { ComponentProps } from "react";
import type { TextureOption } from "@domain/constants/ralColors";
import { AccessoriesOptions } from "@ui/components/AccessoriesOptions";
import { PrestigeAccessoryPanel } from "@ui/components/PrestigeAccessoryPanel";
import { PrestigeDimensionControls } from "@ui/components/PrestigeDimensionControls";
import { SceneSelector } from "@ui/components/SceneSelector";
import { SummaryTab } from "@ui/components/SummaryTab";
import { StyleTabControls } from "./StyleTabControls";

export type ConfigTab =
  | "style"
  | "dimensions"
  | "lighting"
  | "accessories"
  | "scene"
  | "summary";

type DimensionControlsProps = ComponentProps<typeof PrestigeDimensionControls>;
type AccessoryPanelProps = ComponentProps<typeof PrestigeAccessoryPanel>;
type SceneSelectorProps = ComponentProps<typeof SceneSelector>;
type SummaryProps = ComponentProps<typeof SummaryTab>;

type ConfiguratorTabContentProps = {
  activeTab: ConfigTab;
  styleGap?: number;
  styleProps: {
    hasWall: boolean;
    frameTexture: string;
    blindTexture: string;
    blindRotation: number;
    onWallChange: (value: boolean) => void;
    onFrameTextureSelect: (texture: TextureOption) => void;
    onBlindTextureSelect: (texture: TextureOption) => void;
    onBlindRotationChange: (value: number) => void;
  };
  dimensionsProps: {
    widthValue: DimensionControlsProps["widthValue"];
    depthValue: DimensionControlsProps["depthValue"];
    heightValue: DimensionControlsProps["heightValue"];
    unit: DimensionControlsProps["unit"];
    showDimensions: DimensionControlsProps["showDimensions"];
    onWidthChange: DimensionControlsProps["onWidthChange"];
    onDepthChange: DimensionControlsProps["onDepthChange"];
    onHeightChange: DimensionControlsProps["onHeightChange"];
    onUnitChange: DimensionControlsProps["onUnitChange"];
    onShowDimensionsChange: DimensionControlsProps["onShowDimensionsChange"];
    onDownloadDrawings: DimensionControlsProps["onDownloadDrawings"];
  };
  lightingProps: {
    ledsVisible: boolean;
    onLedToggle: () => void;
    heaterQuantity: number;
    onHeaterQtyChange: (qty: number) => void;
  };
  accessoriesProps: {
    widthValue: AccessoryPanelProps["widthValue"];
    roomCount: AccessoryPanelProps["roomCount"];
    surfacePartList: AccessoryPanelProps["surfacePartList"];
    onRemovePart: AccessoryPanelProps["onRemovePart"];
    canAddPart: AccessoryPanelProps["canAddPart"];
    onCustomizePart: AccessoryPanelProps["onCustomizePart"];
    activeAccessoryType: AccessoryPanelProps["activeAccessoryType"];
    isAddMode: AccessoryPanelProps["isAddMode"];
    onSelectAccessory: AccessoryPanelProps["onSelectAccessory"];
    onExitAddMode: AccessoryPanelProps["onExitAddMode"];
    onResetAccessories: AccessoryPanelProps["onResetAccessories"];
  };
  sceneProps: {
    currentScene: SceneSelectorProps["currentScene"];
    onSceneChange: SceneSelectorProps["onSceneChange"];
    visible?: SceneSelectorProps["visible"];
    onVisibleChange?: SceneSelectorProps["onVisibleChange"];
  };
  summaryProps: SummaryProps;
};

export const ConfiguratorTabContent = ({
  activeTab,
  styleProps,
  dimensionsProps,
  lightingProps,
  accessoriesProps,
  sceneProps,
  summaryProps,
  styleGap,
}: ConfiguratorTabContentProps) => {
  if (activeTab === "style") {
    return (
      <StyleTabControls
        hasWall={styleProps.hasWall}
        frameTexture={styleProps.frameTexture}
        blindTexture={styleProps.blindTexture}
        blindRotation={styleProps.blindRotation}
        onWallChange={styleProps.onWallChange}
        onFrameTextureSelect={styleProps.onFrameTextureSelect}
        onBlindTextureSelect={styleProps.onBlindTextureSelect}
        onBlindRotationChange={styleProps.onBlindRotationChange}
        gap={styleGap}
      />
    );
  }

  if (activeTab === "dimensions") {
    return (
      <PrestigeDimensionControls
        widthValue={dimensionsProps.widthValue}
        depthValue={dimensionsProps.depthValue}
        heightValue={dimensionsProps.heightValue}
        unit={dimensionsProps.unit}
        showDimensions={dimensionsProps.showDimensions}
        onWidthChange={dimensionsProps.onWidthChange}
        onDepthChange={dimensionsProps.onDepthChange}
        onHeightChange={dimensionsProps.onHeightChange}
        onUnitChange={dimensionsProps.onUnitChange}
        onShowDimensionsChange={dimensionsProps.onShowDimensionsChange}
        onDownloadDrawings={dimensionsProps.onDownloadDrawings}
      />
    );
  }

  if (activeTab === "lighting") {
    return (
      <AccessoriesOptions
        title="Accessories"
        ledsVisible={lightingProps.ledsVisible}
        onLedToggle={lightingProps.onLedToggle}
        heaterQuantity={lightingProps.heaterQuantity}
        onHeaterQtyChange={lightingProps.onHeaterQtyChange}
      />
    );
  }

  if (activeTab === "accessories") {
    return (
      <PrestigeAccessoryPanel
        widthValue={accessoriesProps.widthValue}
        roomCount={accessoriesProps.roomCount}
        surfacePartList={accessoriesProps.surfacePartList}
        onRemovePart={accessoriesProps.onRemovePart}
        canAddPart={accessoriesProps.canAddPart}
        onCustomizePart={accessoriesProps.onCustomizePart}
        activeAccessoryType={accessoriesProps.activeAccessoryType}
        isAddMode={accessoriesProps.isAddMode}
        onSelectAccessory={accessoriesProps.onSelectAccessory}
        onExitAddMode={accessoriesProps.onExitAddMode}
        onResetAccessories={accessoriesProps.onResetAccessories}
      />
    );
  }

  if (activeTab === "scene") {
    return (
      <SceneSelector
        currentScene={sceneProps.currentScene}
        onSceneChange={sceneProps.onSceneChange}
        visible={sceneProps.visible}
        onVisibleChange={sceneProps.onVisibleChange}
      />
    );
  }

  if (activeTab === "summary") {
    return (
      <SummaryTab
        widthValue={summaryProps.widthValue}
        depthValue={summaryProps.depthValue}
        heightValue={summaryProps.heightValue}
        roomCount={summaryProps.roomCount}
        hasWall={summaryProps.hasWall}
        unit={summaryProps.unit}
        ledsVisible={summaryProps.ledsVisible}
        frameTexture={summaryProps.frameTexture}
        louverTexture={summaryProps.louverTexture}
        surfacePartList={summaryProps.surfacePartList}
      />
    );
  }

  return null;
};
